import { useEffect, useState } from 'react';
import {
  formatDifficulty,
  formatServings,
  formatTotalDuration,
  joinQuantityUnit,
  type SharedRecipeResponse,
} from '@mlc/shared';
import { ApiError, getSharedRecipe } from './api';
import { Banner, Loading, LogoLockup } from './ui';

/**
 * Recette ouverte par un lien de partage (US-33).
 *
 * Écran délibérément **nu** : ni navigation, ni bibliothèque, ni journal. La
 * personne qui ouvre ce lien n'a pas de carnet ici, et lui montrer les entrées
 * d'une application qu'elle ne possède pas serait au mieux confus, au pire une
 * invitation à cliquer sur des portes fermées.
 *
 * Ce qu'il ne montre pas est aussi important : ni notes personnelles, ni
 * journal de réalisations, ni statut de rangement — le serveur ne les envoie
 * même pas. On partage une recette, pas un carnet.
 */
export function SharedRecipe({ token }: { token: string }): React.JSX.Element {
  const [recipe, setRecipe] = useState<SharedRecipeResponse | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getSharedRecipe(token, controller.signal)
      .then(setRecipe)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        // `ApiError` porte le code stable du serveur : c'est lui qui
        // distingue un lien révoqué d'un lien inexistant.
        const code = e instanceof ApiError ? e.code : '';
        setError({ code, message: e instanceof Error ? e.message : String(e) });
      });
    return () => { controller.abort(); };
  }, [token]);

  if (error !== null) {
    return (
      <main className="mx-auto flex min-h-full max-w-form flex-col justify-center gap-4 px-6 py-12">
        {/* Un lien révoqué et un lien inexistant ne disent pas la même chose à
            celui qui l'a reçu : le premier a été valable. */}
        <Banner tone={error.code === 'lien_revoque' ? 'warning' : 'danger'}>{error.message}</Banner>
        <p className="text-sm text-foreground-secondary">
          {error.code === 'lien_revoque'
            ? 'Demandez à la personne qui vous l’a envoyé de le réactiver.'
            : 'Vérifiez le lien : il est peut-être incomplet.'}
        </p>
      </main>
    );
  }

  if (recipe === null) {
    return <main className="mx-auto w-full max-w-reading px-6 py-12"><Loading label="la recette partagée" /></main>;
  }

  const total = formatTotalDuration(recipe);
  const meta = [
    formatServings(recipe.servings, recipe.servings_label),
    total !== null ? `${total.label} au total` : null,
    formatDifficulty(recipe.difficulty),
  ].filter((part): part is string => part !== null);

  return (
    /*
      **La seule page que voit quelqu'un qui n'a pas de compte** (refonte
      éditoriale). Elle héritait d'une composition d'écran interne : titre à
      24 px, photo en 4:3 posée au-dessus, méta en gris — soit exactement ce
      que la fiche recette a cessé d'être. Or c'est par elle que l'application
      se présente à qui ne la connaît pas ; c'est la page qui doit donner
      envie d'en demander l'accès.

      Le même héros que la fiche interne, sans les gestes qui supposent un
      compte : photo, nom du plat en `text-display`, chapeau, repères.
    */
    <main className="mx-auto flex min-h-full max-w-reading flex-col gap-10 px-6 py-12">
      <article className="flex flex-col gap-8" aria-label={`Recette partagée : ${recipe.title}`}>
        <header className="flex flex-col gap-5">
          {recipe.image_path !== null && (
            <img
              src={`/api/partage/${token}/photo`}
              alt=""
              className="aspect-[3/2] w-full rounded-card object-cover shadow-raised"
            />
          )}
          <h1 className="text-display">{recipe.title}</h1>
          {recipe.description !== null && <p className="text-lede">{recipe.description}</p>}
          {meta.length > 0 && (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta">
              {meta.map((part, index) => (
                <span key={part} className="flex items-center gap-3">
                  {index > 0 && <span aria-hidden="true" className="h-3 w-px bg-border-strong" />}
                  {part}
                </span>
              ))}
            </p>
          )}
        </header>

        {recipe.ingredients.length > 0 && (
          <section aria-labelledby="titre-ingredients" className="flex flex-col gap-2">
            <h2 id="titre-ingredients" className="text-heading-2">Ingrédients</h2>
            <ul className="flex flex-col gap-1">
              {recipe.ingredients.map((ingredient) => (
                <li key={ingredient.id}>
                  {ingredient.quantity !== null && (
                    <span className="text-data">
                      {joinQuantityUnit(String(ingredient.quantity), ingredient.unit, ingredient.quantity)}{' '}
                    </span>
                  )}
                  {ingredient.name}
                  {ingredient.note !== null && <span className="text-foreground-muted"> ({ingredient.note})</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {recipe.steps.length > 0 && (
          <section aria-labelledby="titre-etapes" className="flex flex-col gap-3">
            <h2 id="titre-etapes" className="text-heading-2">Étapes</h2>
            <ol className="flex flex-col gap-3">
              {recipe.steps.map((step, index) => (
                <li key={step.id} className="flex gap-3">
                  <span className="text-data shrink-0 font-semibold text-primary">{index + 1}.</span>
                  <span>{step.instruction}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {(recipe.author !== null || recipe.source_name !== null || recipe.source_url !== null) && (
          <p className="text-sm text-foreground-muted">
            {recipe.author !== null && <>De {recipe.author}</>}
            {recipe.author !== null && (recipe.source_name !== null || recipe.source_url !== null) && ' · '}
            {recipe.source_url !== null ? (
              <a href={recipe.source_url} target="_blank" rel="noreferrer" className="underline">
                {recipe.source_name ?? recipe.source_url}
              </a>
            ) : (
              recipe.source_name
            )}
          </p>
        )}
      </article>

      {/* Pied de page de marque : la personne qui ouvre ce lien ne connaît pas
          forcément l'application. Signature sobre, sans lien ni invitation à
          s'inscrire — il n'y a rien à rejoindre (§1 : pas de multi-utilisateur),
          et l'écran reste ce qu'il doit être : une recette, pas une vitrine. */}
      <footer className="mt-auto flex flex-col items-center gap-3 border-t border-border pt-8 pb-2">
        <LogoLockup markSize={48} wordHeight={22} />
        <p className="text-meta">Recette partagée depuis un carnet Mijoté.</p>
      </footer>
    </main>
  );
}
